/**
 * Storage Engine
 * Ported from: innovation-claude/packages/storage/src/engine.rs
 *
 * Facade for multiple storage backends with typed document API.
 */

import { LocalBackend } from './backends/LocalBackend'
import { SessionBackend } from './backends/SessionBackend'
import { RemoteBackend } from './backends/RemoteBackend'
import { RemoteBackupBackend } from './backends/RemoteBackupBackend'
import { StorageDocument } from './StorageDocument'

/**
 * Internal backend discriminated union
 */
type Backend =
    | { type: 'local', backend: LocalBackend }
    | { type: 'session', backend: SessionBackend }
    | { type: 'remote', backend: RemoteBackend }
    | { type: 'remoteBackup', backend: RemoteBackupBackend }

/**
 * StorageEngine - Facade for multiple storage backends
 *
 * Factory methods:
 * - local() - Uses localStorage (persistent)
 * - session() - Uses sessionStorage (tab-scoped)
 * - remote() - Uses HTTP API
 * - remoteBackup() - Uses localStorage + HTTP API (resilient)
 *
 * Example:
 * ```typescript
 * const engine = StorageEngine.local()
 * const doc = engine.document<UserPrefs>('user-prefs')
 * await doc.save({ locale: 'en' })
 * ```
 *
 * Ported from engine.rs:33-187
 */
export class StorageEngine {
    private constructor(private backend: Backend) {}

    /**
     * Create local storage engine
     * Uses browser localStorage (persistent)
     * Ported from engine.rs:45-52
     */
    static local(): StorageEngine {
        return new StorageEngine({
            type: 'local',
            backend: new LocalBackend()
        })
    }

    /**
     * Create session storage engine
     * Uses browser sessionStorage (cleared on tab close)
     * Ported from engine.rs:55-62
     */
    static session(): StorageEngine {
        return new StorageEngine({
            type: 'session',
            backend: new SessionBackend()
        })
    }

    /**
     * Create remote storage engine
     * Uses Tradovate storage HTTP API
     * Ported from engine.rs:84-98
     *
     * @param baseUrl - Storage API base URL (e.g., "https://config-d.tradovateapi.com/")
     * @param getAuthToken - Function that returns current auth token (supports renewal)
     * @param getUserId - Function that returns current user ID
     * @param getUsername - Function that returns current username
     */
    static remote(
        baseUrl: string,
        getAuthToken: () => string,
        getUserId: () => string,
        getUsername: () => string
    ): StorageEngine {
        return new StorageEngine({
            type: 'remote',
            backend: new RemoteBackend(baseUrl, getAuthToken, getUserId, getUsername)
        })
    }

    /**
     * Create remote backup engine
     * Combines local (primary) and remote (backup) storage
     * Ported from engine.rs:131-145
     *
     * Write: localStorage + HTTP API
     * Read: localStorage first → HTTP API fallback
     *
     * @param baseUrl - Storage API base URL
     * @param getAuthToken - Function that returns current auth token (supports renewal)
     * @param getUserId - Function that returns current user ID
     * @param getUsername - Function that returns current username
     */
    static remoteBackup(
        baseUrl: string,
        getAuthToken: () => string,
        getUserId: () => string,
        getUsername: () => string
    ): StorageEngine {
        return new StorageEngine({
            type: 'remoteBackup',
            backend: new RemoteBackupBackend(baseUrl, getAuthToken, getUserId, getUsername)
        })
    }

    /**
     * Create a typed document bound to this engine
     * Ported from engine.rs:160-165
     *
     * @param key - Storage key (e.g., "user-prefs", "workspaces:main")
     * @returns Typed document that delegates to this engine
     */
    document<T>(key: string): StorageDocument<T> {
        return new StorageDocument<T>(key, this)
    }


    /**
     * Internal method to save raw JSON
     * Delegates to backend
     * Ported from engine.rs:190-196
     */
    async saveRaw(key: string, json: string): Promise<void> {
        return this.backend.backend.save(key, json)
    }

    /**
     * Internal method to load raw JSON
     * Delegates to backend
     * Ported from engine.rs:199-205
     */
    async loadRaw(key: string): Promise<string | null> {
        return this.backend.backend.load(key)
    }

    /**
     * Internal method to delete
     * Delegates to backend
     * Ported from engine.rs:208-214
     */
    async deleteRaw(key: string): Promise<void> {
        return this.backend.backend.delete(key)
    }
}
